import React from 'react';
import { AllProjectsIcon, MyProjectsIcon, RecentProjectsIcon, SharedWithMeIcon } from '../../components/Iconset';
import { CreateProjectTypes } from '../../globals';
import CreateScenarioDialog from '../../components/welcome/CreateScenarioDialog';
import ModalManager from '../../components/layout/ModalManager';
import { IProjectInfo } from '../../interfaces/ScenarioElements';
import APIUtility from '../APIUtility';
import { Save } from '../../interfaces/Responses';
import MessagePrompter, { SpecificErrors, SpecificMessages } from '../../components/prompts/MessagePrompter';
import store, { StoreActions } from '../../state/store';
import { ProjectStates } from '../../interfaces/ProjectStates';
import ProjectLoader from '../ProjectLoader';
import ProjectLoadingDialog from '../../components/dialog/ProjectLoadingDialog';
import Dialog from '../../components/layout/Dialog';
import { ProjectListFilters } from '../../interfaces/enums';
import Scenario from '../scenarioElements/Scenario';
import AnalyticsManager from '../AnalyticsManager';
import StandbyOverlay from '../../components/layout/StandbyOverlay';

export const ProjectFilterToIcon =
{
	[ProjectListFilters.recent]: <RecentProjectsIcon />,
	[ProjectListFilters.all_projects]: <AllProjectsIcon />,
	[ProjectListFilters.my_projects]: <MyProjectsIcon />,
	[ProjectListFilters.shared_with_me]: <SharedWithMeIcon />,
};

export function displayCreateScenarioDialog(projectType: CreateProjectTypes)
{
	ModalManager.addModal({
		content: <CreateScenarioDialog projectType={projectType} />
	});
}

export function getLatestManualSave(projectInfo: IProjectInfo, onError: (error: string) => void, callback?: (saveData: any) => void)
{
	if (!projectInfo) onError("Missing project info");

	// call load project
	APIUtility.loadSaves(projectInfo._id, 1)
		.then((data) =>
		{
			if (data?.saves?.manual?.length)
			{
				const save = data.saves.manual[data.saves.manual.length - 1];
				if (save && callback) callback(save);

				return save;
			}
			else onError("No save data found for the project");
		})
		.catch((error) =>
		{
			console.error("failed to retrieve project saves for " + projectInfo._id);
			console.error(error);
			onError("Failed to retrieve project saves for " + projectInfo._id);
		});
}

export function openSaveFile(save: Save, projectData: IProjectInfo)
{
	if (!save)
	{
		MessagePrompter.showError(SpecificErrors.Error20003_ScenarioLoadFailed);
		return;
	}

	AnalyticsManager.managedEvent('openScenario');
	StandbyOverlay.startStandbyMode("Loading Project");

	store.dispatch(StoreActions.scenario.setScenario(new Scenario(projectData).id));
	store.dispatch(StoreActions.assets.setProjectAssets(save.assets));
	store.dispatch(StoreActions.scenario.setProjectState(projectData.state as ProjectStates));
	store.dispatch(StoreActions.scenario.setProjectSaveFileId(save.revisionId));
	const projectLoader = new ProjectLoader();
	const loaderModalId = ModalManager.addModal({
		allowClickOut: false,
		content: <ProjectLoadingDialog
			staticWidth={false}
			loader={projectLoader}
			projectName={projectData.name}
		/>
	});
	projectLoader.load(save.url, save.project).then(() =>
	{
		ModalManager.removeModal(loaderModalId);
	});
}

export function openProjectFromLatestSave(projectInfo: IProjectInfo)
{
	if (!projectInfo) return;

	StandbyOverlay.startStandbyMode();

	// call load project
	APIUtility.loadSaves(projectInfo._id, 1)
		.then((data) =>
		{
			//console.log(data);
			store.dispatch(StoreActions.scenario.setCurrentStep(""))
			StandbyOverlay.stopStandbyMode();

			const latestManualSave = data.saves?.manual?.at(data.saves.manual.length - 1);
			const latestAutoSave = data.saves?.auto?.at(data.saves.auto.length - 1);

			// need to decide whcih save to open
			if (latestManualSave && latestAutoSave)
			{
				// If auto save is more recent, ask user which one
				if (latestManualSave.creationDate < latestAutoSave.creationDate)
				{
					ModalManager.addModal({
						content: <Dialog
							title="Choose Version"
							actions={[
								{
									label: "Open Manual Save",
									onClick: () => { openSaveFile(latestManualSave, projectInfo); },
								},
								{
									label: "Open Newer Auto-Save",
									onClick: () => { openSaveFile(latestAutoSave, projectInfo); },
								}
							]}
						>
							Found a newer version created by Auto-save.
							<br />
							Open this newer version instead of the manually saved one?
						</Dialog>
					});

					return;
				}
			}

			// manual save takes precedence
			const saveToOpen = latestManualSave || latestAutoSave;
			if (saveToOpen)
			{
				openSaveFile(saveToOpen, projectInfo);
			}
			// no save found - error
			else
			{
				MessagePrompter.showError(SpecificErrors.Error20005_ProjectHasNoSaves);
			}
		})
		.catch((error) =>
		{
			console.log("project load failed!");
			console.error(error);
			StandbyOverlay.stopStandbyMode();
		});
}

export function autoLoadProject(projectId: string): void
{
	if (Scenario.current()) return; //if a project is already open, don't auto open another
	if (projectId
		&& !window.location.href.includes("/localhost:3000/") //disables auto load in localhost
	)
	{
		APIUtility.getProject(projectId).then((data: { project: IProjectInfo }) =>
		{
			const foundProject = data.project;
			if (foundProject)
			{
				openProjectFromLatestSave(foundProject);
			}
			else
			{
				MessagePrompter.showMessage(SpecificMessages.Message_CannotAccessProject);
			}
		}).catch((error: any) => 
		{
			console.error("Error fetching project list", error);
			MessagePrompter.showMessage(SpecificMessages.Message_CannotAccessProject);
		});
	}
}